import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { DsAlert, DsButton, DsIcon, DsInput } from "../../../design-system";
import { DATA_ACCESS_LEVELS, LEVELS_WITH_ITEMS } from "./roleAccessUtils";

export default function DataAccessStep({
  dataAccess,
  onChange,
  branches,
  departments,
  allowedLevels,
  ownScope,
  readOnly,
  error,
}) {
  const [query, setQuery] = useState("");
  const level = dataAccess?.level || "";
  const selected = useMemo(() => new Set((dataAccess?.selectedItems || []).map(Number)), [dataAccess]);
  const showPicker = LEVELS_WITH_ITEMS.has(level);
  // A delegate may only pick from their own departments / branches.
  const ownItems = ownScope && ownScope.level === level ? new Set((ownScope.selectedItems || []).map(Number)) : null;
  const items = (level === "department" ? departments : branches) || [];
  const pickable = items.filter((it) => !ownItems || ownItems.has(Number(it.id)));
  const visible = pickable.filter((it) => String(it.name || "").toLowerCase().includes(query.trim().toLowerCase()));

  const setLevel = (value) => {
    if (value === level) return;
    setQuery("");
    onChange({ level: value, selectedItems: [] });
  };
  const toggleItem = (id) => {
    const next = new Set(selected);
    if (next.has(Number(id))) next.delete(Number(id));
    else next.add(Number(id));
    onChange({ level, selectedItems: [...next] });
  };

  return (
    <div className="mx-auto w-full max-w-3xl space-y-5">
      {error ? <DsAlert tone="danger">{error}</DsAlert> : null}
      <div>
        <h4 id="urm-data-h" className="text-sm font-semibold text-[hsl(var(--ds-fg))]">
          Whose records can this role see? *
        </h4>
        <p className="mt-0.5 text-xs text-[hsl(var(--ds-muted-fg))]">
          Applies to every screen granted in the previous step. For example, with “Selected
          departments” a Payroll user sees payroll only for employees in those departments.
        </p>
      </div>

      <div role="radiogroup" aria-labelledby="urm-data-h" className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
        {DATA_ACCESS_LEVELS.map((opt) => {
          const allowed = allowedLevels.has(opt.value);
          const checked = level === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={checked}
              disabled={readOnly || !allowed}
              title={!allowed ? "Wider than your own data access" : undefined}
              onClick={() => setLevel(opt.value)}
              className={`rounded-xl border p-3 text-left transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))] disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none ${
                checked
                  ? "border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary)/0.08)] ring-1 ring-[hsl(var(--ds-primary))]"
                  : "border-[hsl(var(--ds-border))] hover:border-[hsl(var(--ds-primary)/0.45)]"
              }`}
            >
              <span className="flex items-center gap-2">
                <span
                  aria-hidden
                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                    checked ? "border-[hsl(var(--ds-primary))]" : "border-[hsl(var(--ds-border))]"
                  }`}
                >
                  {checked ? <span className="h-2 w-2 rounded-full bg-[hsl(var(--ds-primary))]" /> : null}
                </span>
                <span className="text-sm font-semibold text-[hsl(var(--ds-fg))]">{opt.label}</span>
              </span>
              <span className="mt-1 block pl-6 text-xs leading-snug text-[hsl(var(--ds-muted-fg))]">
                {allowed ? opt.hint : "Wider than your own data access."}
              </span>
            </button>
          );
        })}
      </div>

      {showPicker ? (
        <section className="rounded-xl border border-[hsl(var(--ds-border))]">
          <div className="flex flex-wrap items-center gap-2 border-b border-[hsl(var(--ds-border))] px-3 py-2.5">
            <h5 className="text-sm font-semibold text-[hsl(var(--ds-fg))]">
              {level === "department" ? "Departments" : "Branches / locations"} *
            </h5>
            <span className="text-[11px] text-[hsl(var(--ds-muted-fg))]">{selected.size} selected</span>
            {!readOnly ? (
              <span className="ml-auto flex gap-1.5">
                <DsButton
                  size="sm"
                  variant="ghost"
                  onClick={() => onChange({ level, selectedItems: pickable.map((it) => Number(it.id)) })}
                >
                  Select all
                </DsButton>
                <DsButton size="sm" variant="ghost" onClick={() => onChange({ level, selectedItems: [] })}>
                  Clear
                </DsButton>
              </span>
            ) : null}
          </div>
          {pickable.length > 8 ? (
            <div className="relative border-b border-[hsl(var(--ds-border))] p-2">
              <DsIcon
                icon={Search}
                size="sm"
                className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[hsl(var(--ds-muted-fg))]"
              />
              <DsInput
                type="search"
                aria-label="Search"
                placeholder="Search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-8"
              />
            </div>
          ) : null}
          {pickable.length === 0 ? (
            <p className="p-4 text-sm text-[hsl(var(--ds-muted-fg))]">
              No {level === "department" ? "departments" : "branches"} available. Add them in
              Configuration → Masters first.
            </p>
          ) : (
            <ul className="grid max-h-64 grid-cols-1 gap-1 overflow-y-auto p-2 sm:grid-cols-2">
              {visible.map((it) => (
                <li key={it.id}>
                  <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]">
                    <input
                      type="checkbox"
                      disabled={readOnly}
                      checked={selected.has(Number(it.id))}
                      onChange={() => toggleItem(it.id)}
                      className="h-4 w-4 accent-[hsl(var(--ds-primary))]"
                    />
                    <span className="truncate">{it.name}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}
    </div>
  );
}
